import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getCustomerDetails, 
  addComment, 
  getPvLeadProperties, 
  getWpLeadProperties, 
  getStellungnahmenByCustomer,
  updateCustomerStatus,
  addTermin,
  getTeamsByOrganization,
  getTermineByCustomer,
  updateCustomerData,
  updateTermin,
  cancelTermin,
  downloadConsentForm,
  sendEmail,
  exportCustomerTerminTransferCsv
} from '../api';
import AddCallDialog from '../components/common/AddCallDialog';
import UpdateStatusDialog from '../components/common/UpdateStatusDialog';
import AddProductDialog from '../components/common/AddProductDialog';
import StellungnahmeDetailDialog from './stellungnahmen/StellungnahmeDetailDialog';
import AddTerminDialog from '../components/termine/AddTerminDialog';
import DashboardNav from '../components/dashboard/DashboardNav'; // Importiere DashboardNav
import { getPvConsumptionDisplayFields } from '../utils/pvConsumptionQualification';
import UpdateCustomerDataDialog from '../components/common/UpdateCustomerDataDialog';
import TerminDetailDialog from '../components/termine/TerminDetailDialog';

function CustomerDetail({ user, customerId, onBack, hideNavigation = false, customBackText = "Zurück zur Kundenliste" }) {

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comments, setComments] = useState([]);
  const [calls, setCalls] = useState([]);
  const [teamName, setTeamName] = useState('');
  const [teams, setTeams] = useState([]);

  // Separate Zustände für PV- und WP-Daten
  const [pvLeadData, setPvLeadData] = useState(null);
  const [wpLeadData, setWpLeadData] = useState(null);
  const [pvDataLoading, setPvDataLoading] = useState(true);
  const [wpDataLoading, setWpDataLoading] = useState(true);
  const [pvDataError, setPvDataError] = useState('');
  const [wpDataError, setWpDataError] = useState('');
  
  const navigate = useNavigate();

  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [showAddCallDialog, setShowAddCallDialog] = useState(false);
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const isCallcenterLeader = user?.isCallcenterLeader || false;
  const isCallcenterMitarbeiter = user?.isCallcenterMitarbeiter || false;
  const isOrgLeader = user?.isOrgLeader || false;
  const isAgenturLeader = user?.isAgenturLeader || false;

  // Separate UpdateStatus-Dialoge für PV und WP
  const [showPvUpdateStatusDialog, setShowPvUpdateStatusDialog] = useState(false);
  const [showWpUpdateStatusDialog, setShowWpUpdateStatusDialog] = useState(false);

  // Stellungnahmen-States
  const [customerStellungnahmen, setCustomerStellungnahmen] = useState([]);
  const [stellungnahmenLoading, setStellungnahmenLoading] = useState(false);
  const [stellungnahmenError, setStellungnahmenError] = useState('');
  const [showStellungnahmeDetail, setShowStellungnahmeDetail] = useState(false);
  const [selectedStellungnahme, setSelectedStellungnahme] = useState(null);
  const [stellungnahmen, setStellungnahmen] = useState([]);
  const [showTerminDialog, setShowTerminDialog] = useState(false);
  const [termine, setTermine] = useState([]);
  const [termineLoading, setTermineLoading] = useState(true);
  const [termineError, setTermineError] = useState('');
  // Prüfen, ob Benutzer Admin-Rechte hat
  const isAdmin = user && user.isAdmin;

  // Hilfsvariablen für Callcenter-Rollen
  const isCallcenter = user?.role_type === 4 || user?.role_type === 5;

  const canAddProducts = isAdmin && !isCallcenterLeader && !isCallcenterMitarbeiter;
  const canAddCalls = isAdmin || isCallcenter || isOrgLeader;
  const canUpdateStatus = isAdmin || isOrgLeader || isCallcenterLeader || isCallcenterMitarbeiter;

  const [showUpdateCustomerDataDialog, setShowUpdateCustomerDataDialog] = useState(false);

  const [showEditTerminDialog, setShowEditTerminDialog] = useState(false);
  const [selectedTermin, setSelectedTermin] = useState(null);

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedTerminForCancel, setSelectedTerminForCancel] = useState(null);
  const [stornierungsgrund, setStornierungsgrund] = useState('');

  // Header Actions Dropdown (für mehr Übersicht)
  const [headerActionsOpen, setHeaderActionsOpen] = useState(false);
  const headerActionsRef = useRef(null);

  useEffect(() => {
    if (!headerActionsOpen) return;

    const handleClickOutside = (event) => {
      if (!headerActionsRef.current) return;
      if (!headerActionsRef.current.contains(event.target)) {
        setHeaderActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [headerActionsOpen]);

  // States für den Update-Dialog (aus TerminDetailDialog.js übernommen)
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [newDuration, setNewDuration] = useState(60);
  const [pendingAction, setPendingAction] = useState(null); // null | 'optin' | 'csv'
  const sendingOptInEmail = pendingAction === 'optin';
  const csvExporting = pendingAction === 'csv';

  const [showTerminCsvDialog, setShowTerminCsvDialog] = useState(false);
  const [selectedTerminIdForCsv, setSelectedTerminIdForCsv] = useState(null);

  const fetchCustomerStellungnahmen = async () => {
    setStellungnahmenLoading(true);
    setStellungnahmenError('');

    try {
      const data = await getStellungnahmenByCustomer(user.id, customerId);
      
      if (data.success) {
        setCustomerStellungnahmen(data.stellungnahmen || []);
      } else {
        setStellungnahmenError(data.error || 'Fehler beim Abrufen der Stellungnahmen');
      }
    } catch (err) {
      setStellungnahmenError('Netzwerkfehler: ' + err.message);
    } finally {
      setStellungnahmenLoading(false);
    }
  };

  const fetchCustomerTermine = async () => {
    if (!user || !user.id || !customerId) return;
    setTermineLoading(true);
    setTermineError('');

    try {
      console.log('🏢 API-Aufruf getTermineByCustomer - Organisations-ID:', user.organization_id);
      const data = await getTermineByCustomer(user.id, customerId);
      
      if (data.success) {
        setTermine(data.termine || []);
      } else {
        setTermineError(data.error || 'Fehler beim Laden der Termine');
      }
    } catch (err) {
      setTermineError('Netzwerkfehler: ' + err.message);
    } finally {
      setTermineLoading(false);
    }
  };

  // Kundendetails abrufen
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      setLoading(true);
      setError('');
    
      try {
        console.log('🏢 API-Aufruf getCustomerDetails - Organisations-ID:', user.organization_id);
        console.log("Neue User : ", user)
        const data = await getCustomerDetails(user.id, customerId, user.agentur_id, user.a_team_id);
        if (data.success) {
          setCustomer(data.customer);
          setComments(data.comments || []);
          setCalls(data.calls || []);
        } else {
          console.error("CustomerDetail: Fehler in getCustomerDetails:", data.error);
          setError(data.error || 'Fehler beim Abrufen der Kundendaten');
        }
      } catch (err) {
        console.error("CustomerDetail: Exception in fetchCustomerDetails:", err);
        setError('Netzwerkfehler: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user && user.id && customerId) {
      fetchCustomerDetails();
      fetchCustomerStellungnahmen();
      fetchCustomerTermine();
    } else if (!user) {
      // User wird noch geladen, warten
      return;
    } else {
      console.error("CustomerDetail: Fehlende Props", { userId: user?.id, customerId });
      setError('Fehlende Daten: Benutzer-ID oder Kunden-ID fehlt');
      setLoading(false);
    }
  }, [customerId, user]);

  // PV-Lead-Daten abrufen, wenn der Kunde ein PV-Produkt hat
  useEffect(() => {
    if (!customer) return; // Nur fortfahren, wenn Kundendaten geladen sind
    
    // Prüfen, ob der Kunde ein PV-Produkt hat
    if (customer.produkt && customer.produkt.toLowerCase().includes('pv')) {
      const fetchPvLeadData = async () => {
        setPvDataLoading(true);
        setPvDataError('');
      
        try {
          console.log('🏢 API-Aufruf getPvLeadProperties - Organisations-ID:', user.organization_id);
          const data = await getPvLeadProperties(user.id, customerId);
        
          if (data.success) {
            setPvLeadData(data.properties);
          } else {
            console.error("CustomerDetail: Fehler in getPvLeadProperties:", data.error);
            setPvDataError(data.error || 'Fehler beim Abrufen der PV-Daten');
          }
        } catch (err) {
          console.error("CustomerDetail: Exception in fetchPvLeadData:", err);
          setPvDataError('Netzwerkfehler: ' + err.message);
        } finally {
          setPvDataLoading(false);
        }
      };
  
      fetchPvLeadData();
    } else {
      // Wenn kein PV-Produkt, dann Lade-Status aufheben
      setPvDataLoading(false);
    }
  }, [customer, customerId, user]);

  // WP-Lead-Daten abrufen, wenn der Kunde ein WP-Produkt hat
  useEffect(() => {
    if (!customer) return; // Nur fortfahren, wenn Kundendaten geladen sind
    
    // Prüfen, ob der Kunde ein WP-Produkt hat
    if (customer.produkt && customer.produkt.toLowerCase().includes('wp')) {
      const fetchWpLeadData = async () => {
        setWpDataLoading(true);
        setWpDataError('');
      
        try {
          console.log('🏢 API-Aufruf getWpLeadProperties - Organisations-ID:', user.organization_id);
          const data = await getWpLeadProperties(user.id, customerId);
        
          if (data.success) {
            setWpLeadData(data.properties);
          } else {
            console.error("CustomerDetail: Fehler in getWpLeadProperties:", data.error);
            setWpDataError(data.error || 'Fehler beim Abrufen der WP-Daten');
          }
        } catch (err) {
          console.error("CustomerDetail: Exception in fetchWpLeadData:", err);
          setWpDataError('Netzwerkfehler: ' + err.message);
        } finally {
          setWpDataLoading(false);
        }
      };
  
      fetchWpLeadData();
    } else {
      // Wenn kein WP-Produkt, dann Lade-Status aufheben
      setWpDataLoading(false);
    }
  }, [customer, customerId, user]);

  useEffect(() => {
    const loadTeams = async () => {
      // Prüfe, ob customer und dessen organization_id vorhanden ist
      if (customer && customer.organization_id) {
        try {
          console.log('🏢 API-Aufruf getTeamsByOrganization - Kunden-Organisations-ID:', customer.organization_id);
          const response = await getTeamsByOrganization(user.id, customer.organization_id);
          if (response.success) {
            setTeams(response.teams || []);
            console.log("📋 Teams geladen:", response.teams);
          } else {
            console.warn("⚠️ Fehler beim Laden der Teams:", response.error);
          }
        } catch (err) {
          console.error('❌ Exception beim Laden der Teams:', err);
        }
      }
    };
    
    loadTeams();
  }, [user, customer]);

  const hasPvProduct = customer?.produkt?.toLowerCase().includes('pv') || false;
  const hasWpProduct = customer?.produkt?.toLowerCase().includes('wp') || false;

  // Handler für die Navigation in DashboardNav
  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  useEffect(() => {
    const loadTeams = async () => {
      if (user && user.organization_id) {
        try {
          const response = await getTeamsByOrganization(user.id, user.organization_id);
          if (response.success) {
            setTeams(response.teams || []);
          }
        } catch (err) {
          console.error('Fehler beim Laden der Teams:', err);
        }
      }
    };
    
    loadTeams();
  }, [user]);

  // Validierung nach allen Hooks
  if (!user || !user.id) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Lade Benutzerdaten...</p>
      </div>
    );
  }

  if (!customerId) {
    return (
      <div className="error-container">
        <p>Fehler: Keine Kunden-ID angegeben</p>
      </div>
    );
  }

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/dashboard'); // Oder '/leads' falls es eine eigene Route gibt
    } else if (section === 'termine') {
      navigate('/termine');
    }
  };

  if (loading) {
    return <div className="loading">Kundendaten werden geladen...</div>;
  }

  if (error) {
    console.error("CustomerDetail: Fehler beim Rendern:", error);
    return <div className="error-message">{error}</div>;
  }

  if (!customer) {
    console.error("CustomerDetail: Kein Kunde gefunden für ID:", customerId);
    return <div className="no-data">Kunde nicht gefunden.</div>;
  }

  // Aktualisieren der Kundendaten
  const refreshData = () => {
    // Prüfen, ob User und customerId verfügbar sind
    if (!user || !user.id || !customerId) {
      console.error("CustomerDetail: Kann nicht aktualisieren - fehlende Benutzerdaten");
      return;
    }

    // Kundendetails abrufen
    const fetchCustomerDetails = async () => {
      setLoading(true);
      setError('');
    
      try {
        console.log("Neue User : ", user)
        const data = await getCustomerDetails(user.id, customerId, user.agentur_id, user.a_team_id);
      
        if (data.success) {
          setCustomer(data.customer);
          setComments(data.comments || []);
          setCalls(data.calls || []);
        } else {
          setError(data.error || 'Fehler beim Abrufen der Kundendaten');
        }
      } catch (err) {
        setError('Netzwerkfehler: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    // PV-Lead-Daten abrufen, wenn zutreffend
    const fetchPvLeadData = async () => {
      if (!customer || !(customer.produkt && customer.produkt.toLowerCase().includes('pv'))) return;
      
      setPvDataLoading(true);
      setPvDataError('');
    
      try {
        const data = await getPvLeadProperties(user.id, customerId);
      
        if (data.success) {
          setPvLeadData(data.properties);
        } else {
          setPvDataError(data.error || 'Fehler beim Abrufen der PV-Daten');
        }
      } catch (err) {
        setPvDataError('Netzwerkfehler: ' + err.message);
      } finally {
        setPvDataLoading(false);
      }
    };

    // WP-Lead-Daten abrufen, wenn zutreffend
    const fetchWpLeadData = async () => {
      if (!customer || !(customer.produkt && customer.produkt.toLowerCase().includes('wp'))) return;
      
      setWpDataLoading(true);
      setWpDataError('');
    
      try {
        const data = await getWpLeadProperties(user.id, customerId);
      
        if (data.success) {
          setWpLeadData(data.properties);
        } else {
          setWpDataError(data.error || 'Fehler beim Abrufen der WP-Daten');
        }
      } catch (err) {
        setWpDataError('Netzwerkfehler: ' + err.message);
      } finally {
        setWpDataLoading(false);
      }
    };

    fetchCustomerDetails().then(() => {
      // Nach dem Laden der Kundendetails die Produktdaten abrufen
      fetchPvLeadData();
      fetchWpLeadData();
      fetchCustomerStellungnahmen();
      fetchCustomerTermine();
    });
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    // Prüfen, ob User verfügbar ist
    if (!user || !user.id) {
      setError('Benutzerdaten nicht verfügbar');
      return;
    }
  
    setCommentLoading(true);
  
    try {
      const data = await addComment(user.id, customerId, newComment);
    
      if (data.success) {
        setComments([...comments, data.comment]);
        setNewComment('');
      } else {
        setError(data.error || 'Fehler beim Hinzufügen des Kommentars');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleSaveCall = (newCall) => {
    setCalls([newCall, ...calls]);
    setShowAddCallDialog(false);
    refreshData(); // Daten aktualisieren, um eventuell geänderte Status zu sehen
  };

  const handleUpdatePvStatus = (updatedStatus) => {
    setShowPvUpdateStatusDialog(false);
    refreshData(); // Daten aktualisieren, um neuen Status zu sehen
  };

  const handleUpdateWpStatus = (updatedStatus) => {
    setShowWpUpdateStatusDialog(false);
    refreshData(); // Daten aktualisieren, um neuen Status zu sehen
  }; 

  const handleAddProduct = (updatedProduct) => {
    setShowAddProductDialog(false);
    // Führe einen vollständigen Reload durch, da sich das Produkt geändert hat
    refreshData();
  };

  // Handler für On Hold setzen
  const handleSetOnHold = async () => {
    try {
      const response = await updateCustomerStatus(user.id, customerId, 'On Hold');
      if (response.success) {
        // Daten neu laden
        refreshData();
      } else {
        setError(response.error || 'Fehler beim Setzen auf On Hold');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  // Handler für Final abgesagt setzen
  const handleDownloadConsentForm = async () => {
    try {
      const result = await downloadConsentForm(user.id, customerId);
      if (!result.success) {
        alert('Fehler beim Download: ' + (result.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      console.error('Fehler beim Download der Einverständnisserklärung:', err);
      alert('Fehler beim Download: ' + err.message);
    }
  };

  const openTerminCsvDialog = () => {
    setHeaderActionsOpen(false);
    if (!termine.length) {
      alert('Keine Termine für diesen Kunden vorhanden.');
      return;
    }
    setSelectedTerminIdForCsv(null);
    setShowTerminCsvDialog(true);
  };

  const handleTerminCsvDownload = async () => {
    if (selectedTerminIdForCsv == null) {
      alert('Bitte einen Termin auswählen.');
      return;
    }
    setPendingAction('csv');
    try {
      const response = await exportCustomerTerminTransferCsv(
        user.id,
        customerId,
        [selectedTerminIdForCsv],
        user.agentur_id,
        user.a_team_id
      );
      if (response.success && response.csvData) {
        const binaryString = atob(response.csvData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          response.fileName || `kunde_export_${customerId}_${new Date().toISOString().split('T')[0]}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setShowTerminCsvDialog(false);
      } else {
        alert(response.error || 'Fehler beim Export');
      }
    } catch (err) {
      console.error('CSV-Export Fehler:', err);
      alert('Fehler beim Export: ' + err.message);
    } finally {
      setPendingAction(null);
    }
  };

  const handleSendOptInEmail = async () => {
    if (!customer?.email) {
      alert('Keine E-Mail-Adresse für den Kunden vorhanden.');
      return;
    }

    setPendingAction('optin');
    try {
      const customerName = `${customer.anrede ? customer.anrede + ' ' : ''}${customer.name || ''} ${customer.nachname || ''}`.trim() || customer.email;
      
      const emailData = {
        emailType: 'optin_confirmation',
        recipientEmail: customer.email,
        recipientName: customerName,
        userId: user.id,
        organizationId: customer.organization_id || null,
        // leadId: Backend leitet daraus die Agentur des Lead-Besitzers ab (für Agentur-Branding)
        leadId: customerId
      };

      const result = await sendEmail(emailData);
      
      if (result.success) {
        alert('Opt-In Bestätigungs-E-Mail wurde erfolgreich gesendet!');
      } else {
        alert('Fehler beim Senden der E-Mail: ' + (result.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Fehler: ' + err.message);
      } finally {
        setPendingAction(null);
      }
  };

  const handleSetFinalAbgesagt = async () => {
    try {
      const response = await updateCustomerStatus(user.id, customerId, 'Final abgesagt');
      if (response.success) {
        // Daten neu laden
        refreshData();
      } else {
        setError(response.error || 'Fehler beim Setzen auf Final abgesagt');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleSetFinalNichtErreicht = async () => {
    try {
      const response = await updateCustomerStatus(user.id, customerId, 'Final nicht erreicht');
      if (response.success) {
        refreshData();
      } else {
        setError(response.error || 'Fehler beim Setzen auf Final nicht erreicht');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  // Mindestens 1 Anruf mit Status "Nicht erreicht" → Button "Final nicht erreicht" anzeigen
  const hasNichtErreichtCall = Array.isArray(calls) && calls.some(call => call.status === 'Nicht erreicht');

  // Handler für Termin erstellen
  const handleCreateTermin = () => {
    
    // Prüfe ob alle notwendigen Daten vorhanden sind
    if (!user) {
      console.error("❌ Fehler: User fehlt");
      setError("Fehler: Benutzerdaten fehlen");
      return;
    }
    
    if (!customer) {
      console.error("❌ Fehler: Customer fehlt");
      setError("Fehler: Kundendaten fehlen");
      return;
    }

    setShowTerminDialog(true);
  };

  const handleSaveTermin = async (terminData) => {
    try {

      // Vollständige Termin-Daten zusammenstellen
      const fullTerminData = {
        userId: user.id,
        datum: terminData.datum,
        uhrzeit: terminData.uhrzeit,
        dauer: terminData.dauer || '60',
        kundeId : customerId,
        kunde_name: `${customer.anrede ? customer.anrede + ' ' : ''}${customer.name || ''} ${customer.nachname || ''}`.trim(),
        mitarbeiter_id: terminData.mitarbeiter_id,
        team_id: terminData.team_id,
        team_name: teamName || user.team?.name || 'Unbekanntes Team',
        notizen: terminData.notizen || '',
        art: terminData.art || 'Ersttermin', // Termin-Art hinzugefügt
        adresse: `${customer.strasse || ''} ${customer.hausnummer || ''}, ${customer.postleitzahl || ''} ${customer.stadt || ''}`.trim()
      };

      console.log("📤 Sende Termin-Daten an API:", fullTerminData);
      
      const response = await addTermin(user.id, fullTerminData);
      
      console.log("📥 API Response:", response);
      
      if (response.success) {
        console.log("✅ Termin erfolgreich erstellt");
        
        // Kunde status auf "Terminiert" setzen
        try {
          const statusResponse = await updateCustomerStatus(user.id, customerId, 'Terminiert');
          if (statusResponse.success) {
            console.log("✅ Kundenstatus auf 'Terminiert' geändert");
          } else {
            console.warn("⚠️ Fehler beim Ändern des Kundenstatus:", statusResponse.error);
          }
        } catch (statusErr) {
          console.warn("⚠️ Exception beim Ändern des Kundenstatus:", statusErr);
        }
        
        // Dialog schließen und Daten neu laden
        setShowTerminDialog(false);
        refreshData();
        
      } else {
        console.error("❌ Fehler beim Erstellen des Termins:", response.error);
        setError(response.error || 'Fehler beim Erstellen des Termins');
      }
    } catch (err) {
      console.error("❌ Exception in handleSaveTermin:", err);
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  // Status-Farben-Funktion für Stellungnahmen
  const getStellungnahmeStatusColor = (status) => {
    if (!status) return "";
    
    status = status.toLowerCase();
    if (status.includes('offen') || status.includes('neu')) return "status-new";
    if (status.includes('bearbeitung') || status.includes('in arbeit')) return "status-in-progress";
    if (status.includes('abgeschlossen') || status.includes('erledigt')) return "status-completed";
    if (status.includes('abgelehnt') || status.includes('storniert')) return "status-cancelled";
    return "";
  };

  // Stellungnahme Details anzeigen
  const handleViewStellungnahmeDetail = (stellungnahme) => {
    setSelectedStellungnahme(stellungnahme);
    setShowStellungnahmeDetail(true);
  };

  // Stellungnahme Detail schließen
  const handleCloseStellungnahmeDetail = () => {
    setShowStellungnahmeDetail(false);
    setSelectedStellungnahme(null);
  };

  // Helfer-Funktion für die Anzeige der PV-Daten
  const renderPvData = () => {
    return (
      <div className="pv-data-section">
        <div className="pv-section-header">
          <h1>PV</h1>
          {(isCallcenter || isOrgLeader) && pvLeadData && (
            <button 
              className="update-status-button"
              onClick={() => setShowPvUpdateStatusDialog(true)}
            >
              Status ändern
            </button>
          )}
        </div>
        <div className="pv-grid-container">
          {pvDataLoading ? (
            <div className="loading-message">PV-Daten werden geladen...</div>
          ) : pvDataError ? (
            <div className="error-message">{pvDataError}</div>
          ) : !pvLeadData ? (
            <div className="no-data-message">
              Keine PV-Daten verfügbar
              {canAddCalls && (
                <div className="no-data-action">
                  <button 
                    className="add-product-button"
                    onClick={() => navigate(`/add-pv-lead-to-customer?customerId=${customerId}`)}
                  >
                    PV-Lead hinzufügen
                  </button>
                </div>
              )}
            </div>
          ) : (
            renderPvGrid()
          )}
        </div>
      </div>
    );
  };

  // Helfer-Funktion für die Anzeige der WP-Daten
  const renderWpData = () => {
    return (
      <div className="wp-data-section">
        <div className="wp-section-header">
          <h1>Wärmepumpe</h1>
          {(isCallcenter || isOrgLeader) && wpLeadData && (
            <button 
              className="update-status-button"
              onClick={() => setShowWpUpdateStatusDialog(true)}
            >
              Status ändern
            </button>
          )}
        </div>
        <div className="wp-grid-container">
          {wpDataLoading ? (
            <div className="loading-message">WP-Daten werden geladen...</div>
          ) : wpDataError ? (
            <div className="error-message">{wpDataError}</div>
          ) : !wpLeadData ? (
            <div className="no-data-message">
              Keine Wärmepumpen-Daten verfügbar
              {canAddCalls && (
                <div className="no-data-action">
                  <button 
                    className="add-product-button"
                    onClick={() => navigate(`/add-wp-lead-to-customer?customerId=${customerId}`)}
                  >
                    WP-Lead hinzufügen
                  </button>
                </div>
              )}
            </div>
          ) : (
            renderWpGrid()
          )}
        </div>
      </div>
    );
  };

  const handleEditTermin = (termin) => {
    setSelectedTermin(termin);
    setShowEditTerminDialog(true);
  };

  // Funktion zum Öffnen des Update-Dialogs (aus TerminDetailDialog.js übernommen)
  const openUpdateDialog = (termin) => {
    setSelectedTermin(termin);
    setNewDate(termin.datum);
    setNewTime(termin.startzeit || termin.uhrzeit);
    setNewDuration(termin.dauer || 60);
    setError('');
    setShowUpdateDialog(true);
  };

  const handleSaveEditedTermin = async (updatedTerminData) => {
    try {
      console.log("📝 Original Termin-Daten:", updatedTerminData);
      
      // Berechne die Endzeit basierend auf der Startzeit und Dauer
      const [hours, minutes] = updatedTerminData.uhrzeit.split(':').map(Number);
      const duration = parseInt(updatedTerminData.dauer || '60');
      
      console.log("⏰ Berechnung:", {
        startzeit: updatedTerminData.uhrzeit,
        stunden: hours,
        minuten: minutes,
        dauer: duration
      });
      
      // Berechne die Endzeit in Minuten
      let totalMinutes = hours * 60 + minutes + duration;
      
      // Berechne die neuen Stunden und Minuten
      const endHours = Math.floor(totalMinutes / 60);
      const endMinutes = totalMinutes % 60;
      
      // Formatiere die Endzeit als String
      const endTimeString = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
      
      console.log("⏰ Berechnete Endzeit:", endTimeString);

      // Vollständige Termin-Daten zusammenstellen
      const fullTerminData = {
        datum: updatedTerminData.datum,
        uhrzeit: updatedTerminData.uhrzeit,
        endzeit: endTimeString,
        dauer: duration.toString(),
        mitarbeiter_id: updatedTerminData.mitarbeiter_id,
        team_id: updatedTerminData.team_id,
        notizen: updatedTerminData.notizen || '',
        kundeId: customerId,
        kunde_name: `${customer.anrede ? customer.anrede + ' ' : ''}${customer.name || ''} ${customer.nachname || ''}`.trim()
      };

      console.log("📤 Sende aktualisierte Termin-Daten an API:", fullTerminData);
      
      const response = await updateTermin(user.id, selectedTermin.id, fullTerminData);
      
      if (response.success) {
        console.log("✅ Termin erfolgreich aktualisiert");
        setShowEditTerminDialog(false);
        setSelectedTermin(null);
        refreshData();
      } else {
        console.error("❌ Fehler beim Aktualisieren des Termins:", response.error);
        setError(response.error || 'Fehler beim Aktualisieren des Termins');
      }
    } catch (err) {
      console.error("❌ Exception in handleSaveEditedTermin:", err);
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleCancelTermin = async () => {
    if (!stornierungsgrund.trim()) {
      setError('Bitte geben Sie einen Grund für die Stornierung an.');
      return;
    }

    setCancelling(true);
    setError('');

    try {
      const response = await cancelTermin(user.id, selectedTerminForCancel.id, stornierungsgrund);
      
      if (response.success) {
        setShowCancelDialog(false);
        setSelectedTerminForCancel(null);
        setStornierungsgrund('');
        refreshData();
      } else {
        setError(response.error || 'Fehler beim Stornieren des Termins');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setCancelling(false);
    }
  };

  // handleUpdateTermin Funktion (aus TerminDetailDialog.js übernommen)
  const handleUpdateTermin = async () => {
    if (!newDate || !newTime) {
      setError('Bitte wählen Sie ein neues Datum und eine neue Uhrzeit aus.');
      return;
    }

    setUpdating(true);
    setError('');

    try {
      const response = await updateTermin(user.id, selectedTermin.id, {
        datum: newDate,
        uhrzeit: newTime,
        dauer: newDuration
      });
      
      if (response.success) {
        setShowUpdateDialog(false);
        refreshData(); // Daten neu laden
      } else {
        setError(response.error || 'Fehler beim Aktualisieren des Termins');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const renderTermineData = () => {
    return (
      <div className="detail-card termine-section">
        <div className="termine-header">
          <h3>Termine</h3>
        </div>
        
        <div className="termine-content">
          {termineLoading ? (
            <div className="loading">Termine werden geladen...</div>
          ) : termineError ? (
            <div className="error-message">{termineError}</div>
          ) : termine.length === 0 ? (
            <div className="no-data">Keine Termine für diesen Kunden vorhanden</div>
          ) : (
            <div className="termine-list">
              {termine.map((termin) => (
                <div key={termin.id} className={`termin-item ${termin.status === 'storniert' ? 'termin-cancelled' : ''}`}>
                  <div className="termin-header">
                    <div className="termin-info">
                      <span className={`termin-badge ${termin.status === 'storniert' ? 'status-cancelled' : 'status-active'}`}>
                        {termin.status === 'storniert' ? 'Storniert' : (termin.art || 'Ersttermin')}
                      </span>
                      <div className="termin-datum">
                        {termin.datum && termin.startzeit ? (
                          `${new Date(termin.datum).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit', 
                            year: 'numeric'
                          })} um ${termin.startzeit.substring(0, 5)} Uhr`
                        ) : termin.datum ? (
                          new Date(termin.datum).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })
                        ) : (
                          'Keine Zeit angegeben'
                        )}
                      </div>
                      {termin.endzeit && (
                        <div className="termin-ende">
                          Ende: {termin.endzeit.substring(0, 5)} Uhr
                        </div>
                      )}
                    </div>
                    {(isCallcenterLeader || isCallcenterMitarbeiter) && termin.status !== 'storniert' && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          className="update-status-button"
                          onClick={() => openUpdateDialog(termin)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Termin ändern
                        </button>
                        <button 
                          className="update-status-button"
                          onClick={() => {
                            setSelectedTerminForCancel(termin);
                            setShowCancelDialog(true);
                          }}
                          style={{ backgroundColor: '#e53e3e' }}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                          </svg>
                          Termin stornieren
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {termin.status === 'storniert' && termin.stornierungsgrund && (
                    <div className="termin-cancellation">
                      <strong>Stornierungsgrund:</strong>
                      <div className="cancellation-content">
                        {termin.stornierungsgrund}
                      </div>
                      {termin.storniert_am && (
                        <div className="cancellation-date">
                          Storniert am: {new Date(termin.storniert_am).toLocaleString('de-DE')}
                        </div>
                      )}
                    </div>
                  )}

                  {termin.notizen && (
                    <div className="termin-beschreibung">
                      <strong>Notiz:</strong>
                      <div className="beschreibung-content">
                        {termin.notizen}
                      </div>
                    </div>
                  )}

                  {termin.mitarbeiter_name && (
                    <div className="termin-mitarbeiter">
                      <strong>Mitarbeiter:</strong> {termin.mitarbeiter_name}
                    </div>
                  )}
                  {termin.qc_name && isCallcenterLeader && (
                    <div className="termin-qc">
                      <strong>QC:</strong> {termin.qc_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {showEditTerminDialog && selectedTermin && (
          <AddTerminDialog
            user={user}
            selectedDate={new Date(selectedTermin.datum)}
            selectedTimeSlot={selectedTermin.startzeit}
            selectedTeam={selectedTermin.team_id?.toString() || ''}
            teams={teams}
            preSelectedCustomer={{
              id: customerId,
              name: `${customer.anrede ? customer.anrede + ' ' : ''}${customer.name || ''} ${customer.nachname || ''}`.trim() || 'Unbekannter Kunde',
              email: customer.email || '',
              projekt: customer.projekt || customer.dienstleister || ''
            }}
            onSave={handleSaveEditedTermin}
            onCancel={() => {
              setShowEditTerminDialog(false);
              setSelectedTermin(null);
            }}
            isEditMode={true}
            existingTermin={selectedTermin}
            dialogTitle="Termin ändern"
          />
        )}

        {showTerminCsvDialog && (
          <div className="modal-overlay">
            <div className="modal-dialog" style={{ maxWidth: '480px' }}>
              <div className="modal-header">
                <h3>Termin für CSV wählen</h3>
                <button
                  type="button"
                  className="close-button"
                  onClick={() => setShowTerminCsvDialog(false)}
                  disabled={csvExporting}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="modal-content">
                <p style={{ marginBottom: '12px', color: '#4a5568', fontSize: '0.95em' }}>
                  Wähle genau einen Termin. Die CSV entspricht der Datenübertragung (inkl. Termin-Datum, -Uhrzeit, -Kategorie, -Notiz, Verkäufer).
                </p>
                <div
                  style={{
                    maxHeight: '240px',
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '8px',
                  }}
                >
                  {termine.map((termin) => {
                    let dateLabel = termin.datum || '';
                    if (termin.datum && /^\d{4}-\d{2}-\d{2}$/.test(termin.datum)) {
                      const d = new Date(`${termin.datum}T12:00:00`);
                      dateLabel = d.toLocaleDateString('de-DE');
                    }
                    const timeLabel = termin.startzeit ? String(termin.startzeit).slice(0, 5) : '';
                    return (
                      <label
                        key={termin.id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                          padding: '8px 4px',
                          cursor: csvExporting ? 'default' : 'pointer',
                          borderBottom: '1px solid #f7fafc',
                        }}
                      >
                        <input
                          type="radio"
                          name="termin-csv-export"
                          checked={selectedTerminIdForCsv === termin.id}
                          onChange={() => setSelectedTerminIdForCsv(termin.id)}
                          disabled={csvExporting}
                          style={{ marginTop: '4px' }}
                        />
                        <span style={{ flex: 1, fontSize: '0.95em' }}>
                          <strong>
                            {dateLabel}
                            {timeLabel ? `, ${timeLabel}` : ''}
                          </strong>
                          {termin.art ? ` · ${termin.art}` : ''}
                          {termin.status === 'storniert' ? (
                            <span style={{ color: '#c53030', marginLeft: '6px' }}>(storniert)</span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => setShowTerminCsvDialog(false)}
                    disabled={csvExporting}
                  >
                    Ohne Download Fortfahren
                  </button>
                  <button
                    type="button"
                    className="confirm-button"
                    onClick={handleTerminCsvDownload}
                    disabled={csvExporting || selectedTerminIdForCsv == null}
                  >
                    {csvExporting ? 'Exportiere...' : 'Herunterladen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCancelDialog && (
          <div className="modal-overlay">
            <div className="modal-dialog">
              <div className="modal-header">
                <h3>Termin stornieren</h3>
                <button className="close-button" onClick={() => {
                  setShowCancelDialog(false);
                  setStornierungsgrund('');
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="modal-content">
                <div className="form-group">
                  <label htmlFor="stornierungsgrund">Grund der Stornierung:</label>
                  <textarea
                    id="stornierungsgrund"
                    value={stornierungsgrund}
                    onChange={(e) => setStornierungsgrund(e.target.value)}
                    placeholder="Bitte geben Sie den Grund für die Stornierung an..."
                    rows={4}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button 
                    className="cancel-button"
                    onClick={() => {
                      setShowCancelDialog(false);
                      setStornierungsgrund('');
                    }}
                    disabled={cancelling}
                  >
                    Abbrechen
                  </button>
                  <button 
                    className="confirm-button"
                    onClick={handleCancelTermin}
                    disabled={cancelling || !stornierungsgrund.trim()}
                  >
                    {cancelling ? 'Wird storniert...' : 'Termin stornieren'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Update Termin Dialog (aus TerminDetailDialog.js übernommen) */}
        {showUpdateDialog && selectedTermin && (
          <div className="modal-overlay">
            <div className="modal-dialog">
              <div className="modal-header">
                <h3>Termin ändern</h3>
                <button className="close-button" onClick={() => setShowUpdateDialog(false)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="modal-content">
                <div className="form-group">
                  <label htmlFor="newDate">Neues Datum:</label>
                  <input
                    type="date"
                    id="newDate"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="newTime">Neue Uhrzeit:</label>
                  <input
                    type="time"
                    id="newTime"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="newDuration">Dauer (in Minuten):</label>
                  <select
                    id="newDuration"
                    value={newDuration}
                    onChange={(e) => setNewDuration(Number(e.target.value))}
                  >
                    <option value="30">30 Minuten</option>
                    <option value="60">1 Stunde</option>
                    <option value="90">1,5 Stunden</option>
                    <option value="120">2 Stunden</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button 
                    className="cancel-button"
                    onClick={() => setShowUpdateDialog(false)}
                    disabled={updating}
                  >
                    Abbrechen
                  </button>
                  <button 
                    className="confirm-button update"
                    onClick={handleUpdateTermin}
                    disabled={updating || !newDate || !newTime}
                  >
                    {updating ? 'Wird aktualisiert...' : 'Termin aktualisieren'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          .termine-section {
            margin-bottom: 20px;
          }

          .termine-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }

          .termin-aendern-button:hover {
            background-color: #2a4365;
            transition: all 0.2s ease;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          }

          .termine-list {
            display: flex;
            flex-direction: column;
            gap: 15px;
          }

          .termin-item {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 15px;
            transition: all 0.2s ease;
          }

          .termin-item.termin-cancelled {
            background: #fff5f5;
            border-color: #fed7d7;
          }

          .termin-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
          }

          .termin-info {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .termin-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 0.875rem;
            font-weight: 500;
          }

          .termin-badge.status-active {
            background: #ebf4ff;
            color: #3182ce;
          }

          .termin-badge.status-cancelled {
            background: #fed7d7;
            color: #e53e3e;
          }

          .termin-datum {
            font-weight: 600;
            color: #2d3748;
          }

          .termin-cancellation {
            margin-top: 10px;
            padding: 10px;
            background: #fff5f5;
            border: 1px solid #fed7d7;
            border-radius: 6px;
          }

          .cancellation-content {
            margin-top: 5px;
            color: #e53e3e;
            font-style: italic;
          }

          .cancellation-date {
            margin-top: 5px;
            font-size: 0.875rem;
            color: #718096;
          }

          .termin-beschreibung {
            margin-top: 10px;
          }

          .beschreibung-content {
            margin-top: 5px;
            padding: 8px;
            background: white;
            border-radius: 4px;
            border: 1px solid #e2e8f0;
          }

          .termin-mitarbeiter {
            margin-top: 10px;
            font-size: 0.875rem;
            color: #4a5568;
          }

          .termin-qc {
            margin-top: 8px;
            font-size: 0.875rem;
            color: #4a5568;
            padding: 4px 8px;
            background: #f0f8ff;
            border-radius: 4px;
            border-left: 3px solid #4a6de5;
          }

          @media (max-width: 640px) {
            .termin-header {
              flex-direction: column;
              gap: 8px;
            }

            .termin-info {
              width: 100%;
            }
          }

          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }

          .modal-dialog {
            background: white;
            border-radius: 8px;
            min-width: 300px;
            max-width: 500px;
            width: 100%;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px 20px;
            border-bottom: 1px solid #e2e8f0;
          }

          .modal-header h3 {
            margin: 0;
            color: #2d3748;
            font-size: 1.25rem;
          }

          .modal-content {
            padding: 20px;
          }

          .form-group {
            margin-bottom: 1rem;
          }

          .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: #4a5568;
          }

          .form-group textarea {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid #e2e8f0;
            border-radius: 0.375rem;
            resize: vertical;
            font-size: 0.875rem;
          }

          .modal-actions {
            display: flex;
            justify-content: flex-end;
            gap: 0.75rem;
            margin-top: 1.5rem;
          }

          .cancel-button {
            padding: 0.5rem 1rem;
            border: 1px solid #e2e8f0;
            border-radius: 0.375rem;
            background: white;
            color: #4a5568;
            cursor: pointer;
            font-weight: 500;
          }

          .confirm-button {
            padding: 0.5rem 1rem;
            border: none;
            border-radius: 0.375rem;
            background: #e53e3e;
            color: white;
            cursor: pointer;
            font-weight: 500;
          }

          .confirm-button:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .confirm-button.update {
            background: #3182ce;
          }

          .confirm-button.update:hover {
            background: #2c5282;
          }

          .close-button {
            background: none;
            border: none;
            cursor: pointer;
            color: #718096;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s;
          }

          .close-button:hover {
            background: #e2e8f0;
            color: #2d3748;
          }

          .bewertung-anzeige {
            margin-top: 20px;
            padding: 15px;
            background-color: #f7fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
          }

          .bewertung-anzeige h4 {
            margin: 0 0 10px 0;
            color: #2d3748;
          }

          .bewertung-grid {
            display: grid;
            gap: 10px;
          }

          .bewertung-item p {
            margin: 0;
            padding: 8px;
            background-color: #fff;
            border-radius: 4px;
            border: 1px solid #e2e8f0;
          }
          
          .bewertung-item.meta {
            font-size: 0.8rem;
            color: #718096;
            text-align: right;
          }

          .bewertungen-container {
            display: flex;
            flex-direction: column;
            gap: 20px;
          }

          .bewertung-section {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 15px;
          }

          .bewertung-section h4 {
            margin: 0 0 12px 0;
            color: #2d3748;
            font-size: 1rem;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .bewertung-grid {
            display: grid;
            gap: 10px;
          }

          .bewertung-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .bewertung-item strong {
            color: #4a5568;
            font-size: 0.9rem;
          }

          .bewertung-item p {
            margin: 0;
            padding: 8px;
            background-color: #fff;
            border-radius: 4px;
            border: 1px solid #e2e8f0;
            font-size: 0.9rem;
            line-height: 1.4;
          }
          
          .bewertung-item.meta {
            font-size: 0.8rem;
            color: #718096;
            text-align: right;
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px solid #e2e8f0;
          }
        `}</style>
      </div>
    );
  };

  // Helfer-Funktion zum Rendern von Werten in Kästchen/Badges
  const getStatusColor = (status) => {
    if (!status) return "";
    
    status = status.toLowerCase();
    if (status.includes('offen') || status.includes('neu')) return "status-new";
    if (status.includes('bearbeitung') || status.includes('in arbeit')) return "status-in-progress";
    if (status.includes('abgeschlossen') || status.includes('fertig')) return "status-completed";
    if (status.includes('abgelehnt') || status.includes('storniert')) return "status-cancelled";
    return "";
  };

  const getCustomerStatusColor = (status) => {
    if (!status) return "status-new";
    
    status = status.toLowerCase();
    if (status.includes('erfasst')) return "status-new";
    if (status.includes('on hold') || status.includes('onhold')) return "status-cancelled";
    if (status.includes('terminiert')) return "status-completed";
    if (status.includes('termin abgesagt')) return "status-cancelled";
    if (status.includes('ne kunden')) return "status-cancelled";
    if (status.includes('später anrufen')) return "status-in-progress";
    return "status-new";
  };

  // Helfer-Funktion zum Rendern von Werten in Kästchen/Badges - VERBESSERT
  const renderValue = (value, type = 'text') => {
    if (value === null || value === undefined) return <span>-</span>;
    
    // Text-Werte werden normal angezeigt
    if (type === 'text') {
      return <span className="lead-value-text">{value}</span>;
    }
    
    // Status-Werte verwenden die gleiche Logik wie in der Leads-Übersicht
    if (type === 'status') {
      const statusClass = getStatusColor(value);
      if (statusClass) {
        return <span className={`status-badge ${statusClass}`}>{value}</span>;
      } else {
        return <span className="status-badge status-new">{value}</span>;
      }
    }
    
    // Boolean-Werte
    if (type === 'boolean') {
      let displayValue;
      let badgeClass;
      
      if (value === 'Ja' || value === true || value === 1 || value === '1') {
        badgeClass = 'status-badge-green';
        displayValue = 'Ja';
      } else if (value === 'Nein' || value === false || value === 0 || value === '0') {
        badgeClass = 'status-badge-green'; // Nein für Denkmalschutz und Asbest ist positiv
        displayValue = 'Nein';
      } else {
        return <span className="lead-value-text">{value}</span>;
      }
      
      return <span className={`status-badge ${badgeClass}`}>{displayValue}</span>;
    }
    
    // Andere Typen (building, consumption, etc.)
    let badgeClass = '';
    if (type === 'building') {
      badgeClass = 'status-badge-blue';
    } else if (type === 'consumption') {
      badgeClass = 'status-badge-blue';
    }
    
    if (badgeClass) {
      return <span className={`status-badge ${badgeClass}`}>{value}</span>;
    }
    
    return <span className="lead-value-text">{value}</span>;
  };

  // Helfer-Funktion zum Rendern des PV-Grids
  const renderPvGrid = () => {
    const pvConsumptionDisplay = getPvConsumptionDisplayFields(pvLeadData || {});

    return (
      <div className="pv-grid">
        {/* Erste Zeile */}
        <div className="pv-grid-item">
          <div className="pv-grid-label">Status</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.status, 'status')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">Eigentümer</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.eigentumer, 'boolean')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">Gebäudeart</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.gebaudeart, 'building')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">Dachfläche 20 QM oder größer</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.dachflache_20_qm, 'boolean')}
          </div>
        </div>
        
        {/* Zweite Zeile */}
        <div className="pv-grid-item">
          <div className="pv-grid-label">Dachfläche</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.dachflache, 'text')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">Alternative Montage möglich</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.alternative_montage, 'boolean')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">Alternative Montagefläche</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.alternative_montage_flaeche, 'text')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">Dachausrichtung</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.dachausrichtung, 'text')}
          </div>
        </div>
        
        {/* Dritte Zeile */}
        <div className="pv-grid-item">
          <div className="pv-grid-label">Denkmalschutz</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.denkmalschutz, 'boolean')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">kWh-Verbrauch &gt; 2700?</div>
          <div className="pv-grid-value">
            {renderValue(pvConsumptionDisplay.verbrauchUeber2700, 'boolean')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">kWh 2300–2700 – Realisierung in 3 Monaten?</div>
          <div className="pv-grid-value">
            {renderValue(pvConsumptionDisplay.verbrauch2300_2700_3Monate, 'boolean')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">Jahresverbrauch (kWh)</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.jahresverbrauch, 'text')}
          </div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label">Zukünftige Energie Abnehmer</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.zukuenftige_energie_abnehmer, 'boolean')}
          </div>
        </div>
        
        {/* Vierte Zeile */}
        <div className="pv-grid-item">
          <div className="pv-grid-label">Asbest</div>
          <div className="pv-grid-value">
            {renderValue(pvLeadData.asbest, 'boolean')}
          </div>
        </div>
        {/* Leere Zellen für gleichmäßiges Grid */}
        <div className="pv-grid-item">
          <div className="pv-grid-label"></div>
          <div className="pv-grid-value"></div>
        </div>
        <div className="pv-grid-item">
          <div className="pv-grid-label"></div>
          <div className="pv-grid-value"></div>
        </div>
      </div>
    );
  };

  // Helfer-Funktion zum Rendern des WP-Grids
  const renderWpGrid = () => {
    return (
      <div className="wp-grid">
        {/* Erste Zeile */}
        <div className="wp-grid-item">
          <div className="wp-grid-label">Status</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.status, 'status')}
          </div>
        </div>
        <div className="wp-grid-item">
          <div className="wp-grid-label">Eigentümer</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.eigentumer, 'boolean')}
          </div>
        </div>
        <div className="wp-grid-item">
          <div className="wp-grid-label">Gebäudeart</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.gebaudeart, 'building')}
          </div>
        </div>
        <div className="wp-grid-item">
          <div className="wp-grid-label">Wohnfläche</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.wohnflache, 'text')}
          </div>
        </div>
        
        {/* Zweite Zeile */}
        <div className="wp-grid-item">
          <div className="wp-grid-label">Personen im Haus</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.personen, 'text')}
          </div>
        </div>
        <div className="wp-grid-item">
          <div className="wp-grid-label">Baujahr</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.baujahr, 'text')}
          </div>
        </div>
        <div className="wp-grid-item">
          <div className="wp-grid-label">Gas oder Öl Heizung</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.gas_oder_oel, 'boolean')}
          </div>
        </div>
        <div className="wp-grid-item">
          <div className="wp-grid-label">Heizung älter als 2 Jahre</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.heizung_alter_2, 'boolean')}
          </div>
        </div>
        
        {/* Dritte Zeile */}
        <div className="wp-grid-item">
          <div className="wp-grid-label">Wärmeabgabesystem</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.waermeabgabesystem, 'text')}
          </div>
        </div>
        <div className="wp-grid-item">
          <div className="wp-grid-label">Solarthermie</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.solarthermie, 'boolean')}
          </div>
        </div>
        <div className="wp-grid-item">
          <div className="wp-grid-label">Solarthermie-Abbau</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.solarthermie_abbau, 'boolean')}
          </div>
        </div>
        <div className="wp-grid-item">
          <div className="wp-grid-label">Denkmalschutz</div>
          <div className="wp-grid-value">
            {renderValue(wpLeadData.denkmalschutz, 'boolean')}
          </div>
        </div>
      </div>
    );
  };

  const handleUpdateCustomerData = async (updatedData) => {
    try {
      const response = await updateCustomerData(user.id, customerId, updatedData);
      
      if (response.success) {
        setShowUpdateCustomerDataDialog(false);
        refreshData(); // Daten neu laden
      } else {
        setError(response.error || 'Fehler beim Aktualisieren der Kundendaten');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  if (loading) {
    return <div className="loading">Kundendaten werden geladen...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!customer) {
    return <div className="no-data">Kunde nicht gefunden.</div>;
  }

  return (
    <>
      {!hideNavigation && (
        <DashboardNav 
          activeSection="leads"
          onSectionChange={handleSectionChange}
          onLogout={handleLogout}
          user={user}
          onOpenOrganizationManagement={() => {}}
          onOpenMembersManagement={() => {}}
          onViewOrganizationLeads={() => {}}
          onViewTeamLeads={() => {}}
          showOrgManagement={user?.isAdmin || user?.isOrgLeader}
          showMembersManagement={user?.isAdmin || user?.isOrgLeader}
          showOrganizationLeads={user?.isAdmin || user?.isOrgLeader}
        />
      )}
      <div className="customer-detail-page">
        {/* Der obere Bereich mit Zurück-Button und Kundenname bleibt bestehen, 
            da DashboardNav die allgemeine Navigation abdeckt. */}
        <div className="detail-header">
          <div className="detail-header-left">
            <button className="back-button" onClick={onBack}>
              {customBackText}
            </button>
          </div>
          <div className="customer-name">
            {customer.anrede && customer.anrede !== '-' ? `${customer.anrede} ` : ''}
            {customer.name || ''} {customer.nachname || ''}
          </div>
          <div className="detail-header-actions">
            <div ref={headerActionsRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button
                className="actions-trigger-button"
                onClick={() => setHeaderActionsOpen(v => !v)}
                title="Aktionen"
                aria-label="Aktionen öffnen"
              >
                <span className="actions-trigger-icon">⋮</span>
              </button>

              {headerActionsOpen && (
                <div
                  className="actions-dropdown-menu"
                >
                  <div className="actions-dropdown-content">
                    {/* Callcenter Buttons - nur für Callcenter Leader und Mitarbeiter */}
                    {(isCallcenterLeader || isCallcenterMitarbeiter || isOrgLeader) &&
                      Array.isArray(termine) &&
                      termine.some(t => t.status === 'storniert') && (
                        <button
                          className="final-abgesagt-button"
                          onClick={() => {
                            setHeaderActionsOpen(false);
                            handleSetFinalAbgesagt();
                          }}
                          disabled={customer.customer_status === 'Final abgesagt'}
                        >
                          Final abgesagt
                        </button>
                      )}

                    {(isCallcenterLeader || isCallcenterMitarbeiter || isOrgLeader) && (
                      <>
                        {hasNichtErreichtCall && (
                          <button
                            className="final-nicht-erreicht-button"
                            onClick={() => {
                              setHeaderActionsOpen(false);
                              handleSetFinalNichtErreicht();
                            }}
                            disabled={customer.customer_status === 'Final nicht erreicht'}
                          >
                            Final nicht erreicht
                          </button>
                        )}
                        <button
                          className="on-hold-button"
                          onClick={() => {
                            setHeaderActionsOpen(false);
                            handleSetOnHold();
                          }}
                          disabled={customer.customer_status === 'On Hold'}
                        >
                          On Hold
                        </button>
                        <button
                          className="termin-plus-button"
                          onClick={() => {
                            setHeaderActionsOpen(false);
                            handleCreateTermin();
                          }}
                        >
                          Termin+
                        </button>
                      </>
                    )}

                    {(isAdmin || isOrgLeader) && (
                      <button
                        className="update-status-button"
                        onClick={() => {
                          setHeaderActionsOpen(false);
                          handleDownloadConsentForm();
                        }}
                        style={{ backgroundColor: '#1d72b8', color: 'white' }}
                      >
                        PDF Downloaden
                      </button>
                    )}

                    <button
                      className="update-status-button"
                      onClick={openTerminCsvDialog}
                      disabled={csvExporting || termineLoading || !termine.length}
                      style={{ backgroundColor: '#0d9488', color: 'white' }}
                      title="Transfer-CSV: Kundendaten plus ausgewählte Termine (Datum, Uhrzeit, Kategorie, Notiz, Verkäufer)"
                    >
                      {csvExporting ? 'Exportiere...' : 'Als CSV exportieren'}
                    </button>

                    {(isCallcenter || isOrgLeader || isAdmin) &&
                      !customer?.signature && customer?.opt_in != 1 && (
                        <button
                          className="update-status-button"
                          onClick={() => {
                            setHeaderActionsOpen(false);
                            handleSendOptInEmail();
                          }}
                          disabled={sendingOptInEmail || !customer?.email}
                          style={{ backgroundColor: '#7c3aed', color: 'white' }}
                          title="Opt-In Bestätigungs-E-Mail an den Kunden senden"
                        >
                          {sendingOptInEmail ? 'Wird gesendet...' : 'OptIn Senden'}
                        </button>
                      )}

                    <button
                      className="refresh-button"
                      onClick={() => {
                        setHeaderActionsOpen(false);
                        refreshData();
                      }}
                    >
                      Aktualisieren
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      
        <div className="detail-row">
          <div className="detail-card">
            <h3>Projekt</h3>
            <div className="card-content">
              {customer.projekt || 'Kein Projekt zugewiesen'}
            </div>
          </div>
          <div className="detail-card">
            <h3>Anrufe</h3>
            <div className="card-content">
              {calls.length > 0 ? (
                <span style={{ 
                  color: (isCallcenterLeader || isCallcenterMitarbeiter) ? '#e53e3e' : 'inherit', 
                  fontWeight: 500,
                  display: 'inline-block'
                }}>
                  {calls.length} Anrufe getätigt
                </span>
              ) : (
                'Keine Anrufe getätigt'
              )}
            </div>
          </div>
        </div>
      
        <div className="detail-card comments-section" style={{
          backgroundColor: '#f0f4f8',
          border: '2px solid #2c5282',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            color: '#2c5282',
            marginBottom: '20px',
            fontSize: '1.4em',
            borderBottom: '2px solid #2c5282',
            paddingBottom: '10px'
          }}>Kommentare</h3>
          <div className="comments-list">
            {comments.length > 0 ? (
              comments.map((comment, index) => (
                <div key={index} className="comment-item" style={{
                  backgroundColor: '#ffffff',
                  borderLeft: '4px solid #2c5282',
                  borderRadius: '4px',
                  padding: '15px',
                  marginBottom: '15px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <div className="comment-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <span className="comment-author" style={{
                      fontWeight: '600',
                      color: '#2d3748'
                    }}>{comment.user_name || 'Unbekannt'}</span>
                    <span className="comment-date" style={{
                      color: '#718096'
                    }}>
                      {new Date(comment.erstellt).toLocaleString('de-DE')}
                    </span>
                  </div>
                  <div className="comment-text" style={{
                    color: '#4a5568',
                    lineHeight: '1.5',
                    fontWeight: '600'
                  }}>{comment.text}</div>
                </div>
              ))
            ) : (
              <div className="no-data" style={{
                textAlign: 'center',
                color: '#718096',
                padding: '20px',
                fontStyle: 'italic'
              }}>Keine Kommentare vorhanden</div>
            )}
          </div>
          
          <div className="comment-form" style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#ffffff',
            borderRadius: '6px',
            border: '1px solid #e2e8f0'
          }}>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Neuen Kommentar hinzufügen..."
              rows={3}
              style={{
                backgroundColor: '#ffffff',
                border: '2px solid #2c5282',
                borderRadius: '6px',
                padding: '12px',
                width: '100%',
                marginBottom: '10px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            ></textarea>
            <button 
              onClick={handleAddComment}
              disabled={commentLoading || !newComment.trim()}
              style={{
                backgroundColor: '#2c5282',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
            >
              {commentLoading ? 'Wird gespeichert...' : 'Kommentar hinzufügen'}
            </button>
          </div>
        </div>
      
        {/* Produktdaten-Bereiche - beide anzeigen, wenn Produkttyp "PV+WP" oder jeweils einer davon */}
        {renderPvData()}
        {renderWpData()}
        {renderTermineData()}

        <div className="detail-card">
          <h3>Backoffice-Kommentar</h3>
          <div className="bo-comment-section">
            {customer.bo_kommentar ? (
              <>
                <div className="bo-comment-content">
                  {customer.bo_kommentar}
                </div>
                {customer.bo_kommentar_erstellt && customer.bo_kommentar_ersteller_name && (
                  <div className="bo-comment-meta">
                    <small>
                      Kommentar von {customer.bo_kommentar_ersteller_name} 
                      am {new Date(customer.bo_kommentar_erstellt).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </small>
                  </div>
                )}
              </>
            ) : (
              <div className="no-data">Kein Backoffice-Kommentar vorhanden</div>
            )}
          </div>

          <style jsx>{`
            .bo-comment-section {
              background: #f8fafc;
              border-radius: 8px;
              padding: 20px;
              margin-top: 10px;
            }

            .bo-comment-content {
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 15px;
              margin-bottom: 10px;
              white-space: pre-wrap;
              line-height: 1.5;
            }

            .bo-comment-meta {
              color: #718096;
              font-style: italic;
              padding: 8px;
              background: #f1f5f9;
              border-radius: 4px;
              border-left: 3px solid #4a6de5;
            }

            .no-data {
              color: #718096;
              font-style: italic;
              text-align: center;
              padding: 20px;
            }
          `}</style>
        </div>
        
        <div className="detail-card">
          <div className="pv-section-header">
            <h3>Kundendaten</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              {isCallcenter && !customer?.signature && customer?.opt_in != 1 && (
                <button
                  className="update-status-button2"
                  onClick={handleSendOptInEmail}
                  disabled={sendingOptInEmail || !customer?.email}
                >
                  {sendingOptInEmail ? 'Wird gesendet...' : 'OptIn Senden'}
                </button>
              )}
              {(isCallcenter || isOrgLeader) && (
                <button 
                  className="update-status-button"
                  onClick={() => setShowUpdateCustomerDataDialog(true)}
                >
                  Daten ändern
                </button>
              )}
            </div>
          </div>
          <div className="customer-data">
            <div className="data-row">
              <div className="data-label">Anrede:</div>
              <div className="data-value">{customer.ansprache || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Vorname:</div>
              <div className="data-value">{customer.name || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Nachname:</div>
              <div className="data-value">{customer.nachname || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">E-Mail:</div>
              <div className="data-value">{customer.email || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Strasse:</div>
              <div className="data-value">{customer.strasse || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Hausnummer:</div>
              <div className="data-value">{customer.hausnummer || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Postleitzahl:</div>
              <div className="data-value">{customer.postleitzahl || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Stadt:</div>
              <div className="data-value">{customer.stadt || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Telefon:</div>
              <div className="data-value">{customer.handy || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Produkt:</div>
              <div className="data-value">{customer.produkt || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Dienstleister:</div>
              <div className="data-value">{customer.dienstleister || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">Projekt:</div>
              <div className="data-value">{customer.projekt || '-'}</div>
            </div>
            <div className="data-row">
              <div className="data-label">OPT IN:</div>
              <div className="data-value">
                <span className={`status-badge ${(customer.signature || customer.opt_in) ? 'status-completed' : 'status-cancelled'}`}>
                  {(customer.signature || customer.opt_in) ? 'Bestätigt' : 'Unbestätigt'}
                </span>
              </div>
            </div>
            <div className="data-row">
              <div className="data-label">Aktueller Status:</div>
              <div className="data-value">
                <span className={`status-badge ${getCustomerStatusColor(customer.customer_status || 'Erfasst')}`}>
                  {customer.customer_status || 'Erfasst'}
                </span>
              </div>
            </div>
            <div className="data-row">
              <div className='data-label'>Opt In Type</div>
              <div className="data-value">
                {customer.signature
                  ? "Unterschrift"
                  : customer.opt_in === 1 || customer.opt_in === "1"
                    ? "E-Mail"
                    : "-"}
              </div>
            </div>
            
            {/* Unterschriften-Bereich für Callcenter und Organisationsleiter */}
            {(isCallcenter || isOrgLeader) && customer.signature && (
              <div className="data-row signature-row" style={{ 
                borderTop: '2px solid #e5e7eb', 
                paddingTop: '15px', 
                marginTop: '15px' 
              }}>
                <div className="data-label" style={{ 
                  fontWeight: '600', 
                  color: '#374151' 
                }}>
                  Unterschrift:
                </div>
                <div className="data-value signature-container" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <div style={{
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: '#f9fafb',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <img 
                      src={customer.signature} 
                      alt="Kundenunterschrift" 
                      className="signature-image"
                      style={{
                        maxWidth: '300px',
                        maxHeight: '150px',
                        display: 'block',
                        borderRadius: '4px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div style={{ 
                      display: 'none',
                      padding: '20px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontStyle: 'italic',
                      border: '1px dashed #d1d5db',
                      borderRadius: '4px'
                    }}>
                      Unterschrift konnte nicht geladen werden
                    </div>
                  </div>
                  <div className="signature-info">
                    <small style={{ 
                      color: '#6b7280', 
                      fontStyle: 'italic',
                      backgroundColor: '#f3f4f6',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      📝 Digitale Unterschrift des Kunden
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="detail-card">
          <h3>Stellungnahmen zu diesem Kunden</h3>
          <div className="card-content">
            {stellungnahmenLoading ? (
              <div className="loading">Stellungnahmen werden geladen...</div>
            ) : stellungnahmenError ? (
              <div className="error-message">{stellungnahmenError}</div>
            ) : customerStellungnahmen.length === 0 ? (
              <div className="no-data">Keine Stellungnahmen für diesen Kunden vorhanden</div>
            ) : (
              <div className="stellungnahmen-list">
                {customerStellungnahmen.map((stellungnahme) => (
                  <div key={stellungnahme.id} className="stellungnahme-item" style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '15px',
                    marginBottom: '10px',
                    backgroundColor: '#f9fafb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <strong>Erstellt am:</strong> {new Date(stellungnahme.erstellt).toLocaleString('de-DE')}
                        <br />
                        <strong>Von:</strong> {stellungnahme.ersteller_name || stellungnahme.ersteller_email || 'Unbekannt'}
                      </div>
                      <span className={`status-badge ${getStellungnahmeStatusColor(stellungnahme.status)}`}>
                        {stellungnahme.status}
                      </span>
                    </div>
                    
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Nachricht:</strong>
                      <div style={{ 
                        marginTop: '5px', 
                        padding: '8px', 
                        backgroundColor: 'white', 
                        borderRadius: '4px',
                        maxHeight: '100px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {stellungnahme.nachricht.length > 200 ? 
                          stellungnahme.nachricht.substring(0, 200) + '...' : 
                          stellungnahme.nachricht
                        }
                      </div>
                    </div>

                    {stellungnahme.antwort && (
                      <div style={{ marginBottom: '10px' }}>
                        <strong>Antwort:</strong>
                        <div style={{ 
                          marginTop: '5px', 
                          padding: '8px', 
                          backgroundColor: '#f0f8f0', 
                          borderRadius: '4px',
                          maxHeight: '100px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {stellungnahme.antwort.length > 200 ? 
                            stellungnahme.antwort.substring(0, 200) + '...' : 
                            stellungnahme.antwort
                          }
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleViewStellungnahmeDetail(stellungnahme)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '0.875rem',
                          border: '1px solid #4a6de5',
                          borderRadius: '4px',
                          backgroundColor: '#4a6de5',
                          color: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        Details anzeigen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Kundenbewertung (Verkaufs-Nachrichten der Vertriebler): nur Admin, Callcenter-Leiter und Callcenter-Mitarbeiter */}
        {(isAdmin || isCallcenter) && (
        <div className="detail-card">
          <h3>Kundenbewertung</h3>
          <div className="card-content">
            {(!pvLeadData?.bewertung_status && !wpLeadData?.bewertung_status) ? (
              <div className="no-data">Bewertung wurde noch nicht gegeben</div>
            ) : (
              <div className="bewertungen-container">
                {/* PV-Bewertung */}
                {pvLeadData?.bewertung_status && (
                  <div className="bewertung-section">
                    <h4>☀️ PV-Bewertung</h4>
                    <div className="bewertung-grid">
                      <div className="bewertung-item">
                        <strong>Status:</strong> 
                        <span className="status-badge">{pvLeadData.bewertung_status}</span>
                      </div>
                      <div className="bewertung-item">
                        <strong>Notiz:</strong> 
                        <p>{pvLeadData.bewertung_notiz}</p>
                      </div>
                      <div className="bewertung-item meta">
                        Bewertet von {pvLeadData.bewertet_von} am {new Date(pvLeadData.bewertet_am).toLocaleString('de-DE')}
                      </div>
                    </div>
                  </div>
                )}

                {/* WP-Bewertung */}
                {wpLeadData?.bewertung_status && (
                  <div className="bewertung-section">
                    <h4>🔥 WP-Bewertung</h4>
                    <div className="bewertung-grid">
                      <div className="bewertung-item">
                        <strong>Status:</strong> 
                        <span className="status-badge">{wpLeadData.bewertung_status}</span>
                      </div>
                      <div className="bewertung-item">
                        <strong>Notiz:</strong> 
                        <p>{wpLeadData.bewertung_notiz}</p>
                      </div>
                      <div className="bewertung-item meta">
                        Bewertet von {wpLeadData.bewertet_von} am {new Date(wpLeadData.bewertet_am).toLocaleString('de-DE')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        <div className="detail-card">
          <h3>Anrufliste</h3>
          {calls.length > 0 ? (
            <table className="calls-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Status</th>
                  {(isCallcenterLeader || isCallcenterMitarbeiter) && (
                    <th>Anrufer</th>
                  )}
                  {(isCallcenterLeader || isCallcenterMitarbeiter) && (
                    <th>Rückruf-Zeit</th>
                  )}
                  <th>Kommentar</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call, index) => (
                  <tr key={index}>
                    <td>{new Date(call.datum).toLocaleString('de-DE')}</td>
                    <td>{call.status}</td>
                    {(isCallcenterLeader || isCallcenterMitarbeiter) && (
                      <td>{call.user_name || 'Unbekannt'}</td>
                    )}
                    {(isCallcenterLeader || isCallcenterMitarbeiter) && (
                      <td>
                        {call.ruckruf_zeit && (call.status === 'Rückruf erwünscht' || call.status === 'Später anrufen') ? (
                          <span style={{ 
                            color: '#4a6de5', 
                            fontWeight: '500',
                            fontSize: '0.9em'
                          }}>
                            {new Date(call.ruckruf_zeit).toLocaleString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        ) : (
                          <span style={{ color: '#718096', fontStyle: 'italic' }}>-</span>
                        )}
                      </td>
                    )}
                    <td>{call.kommentar}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data">Keine Anrufe vorhanden</div>
          )}
          {canAddCalls && (
            <button 
              className="add-call-button"
              onClick={() => setShowAddCallDialog(true)}
            >
              Neuen Anruf hinzufügen
            </button>
          )}
        </div>

        {showStellungnahmeDetail && selectedStellungnahme && (
          <StellungnahmeDetailDialog
            stellungnahme={selectedStellungnahme}
            user={user}
            onClose={handleCloseStellungnahmeDetail}
            onNavigateToCustomer={() => {}} // Leer, da wir bereits in der Kundenansicht sind
          />
        )}

        {/* Unterer Zurück-Button */}
        <div className="detail-actions">
          <button className="back-button" onClick={onBack}>
            {customBackText}
          </button>
        </div>
      
        {showAddCallDialog && canAddCalls && (
          <AddCallDialog
            customerId={customerId}
            userId={user.id}
            onSave={handleSaveCall}
            onCancel={() => setShowAddCallDialog(false)}
          />
        )}

        {/* Separate Dialoge für PV- und WP-Status */}
        {showPvUpdateStatusDialog && canUpdateStatus && (
          <UpdateStatusDialog
            customerId={customerId}
            userId={user.id}
            currentStatus={pvLeadData?.status || 'Offen'}
            onSave={handleUpdatePvStatus}
            onCancel={() => setShowPvUpdateStatusDialog(false)}
            productType="PV"
            leadData={pvLeadData}
          />
        )}

        {showWpUpdateStatusDialog && canUpdateStatus && (
          <UpdateStatusDialog
            customerId={customerId}
            userId={user.id}
            currentStatus={wpLeadData?.status || 'Offen'}
            onSave={handleUpdateWpStatus}
            onCancel={() => setShowWpUpdateStatusDialog(false)}
            productType="WP"
            leadData={wpLeadData}
          />
        )}

        {canAddProducts && (
          <button 
            className="add-product-fab" 
            onClick={() => setShowAddProductDialog(true)}
            title="Produkt hinzufügen"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        )}

        {showAddProductDialog && canAddProducts && (
          <AddProductDialog
            customerId={customerId}
            userId={user.id}
            onSave={handleAddProduct}
            onCancel={() => setShowAddProductDialog(false)}
            existingProduct={customer.produkt}
          />
        )}

        {showTerminDialog && (isCallcenterLeader || isCallcenterMitarbeiter || isOrgLeader) && (
          <AddTerminDialog
            user={user}
            selectedDate={new Date()}
            selectedTimeSlot={null}
            selectedTeam={user.team_id?.toString() || ''}
            teams={teams}
            preSelectedCustomer={{
              id: customerId,
              name: `${customer.anrede ? customer.anrede + ' ' : ''}${customer.name || ''} ${customer.nachname || ''}`.trim() || 'Unbekannter Kunde',
              email: customer.email || '',
              projekt: customer.projekt || customer.dienstleister || ''
            }}
            onSave={handleSaveTermin}
            onCancel={() => {
              console.log("🚫 AddTerminDialog abgebrochen");
              setShowTerminDialog(false);
            }}
          />
        )}

        {showUpdateCustomerDataDialog && (
          <UpdateCustomerDataDialog
            customer={customer}
            user={user}
            onSave={handleUpdateCustomerData}
            onCancel={() => setShowUpdateCustomerDataDialog(false)}
          />
        )}

      </div>
    </>
  );
  
}

export default CustomerDetail;
